import { all, put, takeLatest, call, select } from 'redux-saga/effects';
import MapActionTypes, {
  ArcGISWorldGeocodingSuggestionDataModel
} from './types';
import {
  MAP_DATA_SERVICE_DATA_SET,
  MapPlaceSuggestion,
  HexBinClassBreaks,
  BaseMapItem,
  MAP_DEFAULT_VAR
} from 'types';
import {
  getPlaceSuggestFailure,
  getPlaceSuggestSuccess,
  getBaseMapsSuccess,
  getBaseMapsFailure,
  queryFeatureLayerExtentSuccess,
  queryFeatureLayerExtentFailure,
  queryFeatureLayerFeaturesSuccess,
  queryFeatureLayerFeaturesFailure,
  getHexBinClassBreakStatisticsSuccess,
  getHexBinClassBreakStatisticsFailure,
  getGeocodeSuccess,
  getGeocodeFailure,
  getGeoscapeAddressFailure,
  getGeoscapeAddressSuccess,
  getGeoscapeLandParcelFailure,
  getGeoscapeLandParcelSuccess,
  getPlaceCandidateAddressSuccess,
  getPlaceCandidateAddressFailure
} from './actions';
import { setBaseMap } from 'redux/app/actions';
import {
  getPlaceSuggest,
  queryFeatuerLayerExtent,
  queryFeatuerLayerFeatures,
  getHexBinClassBreakStatistics,
  getGeocodeByCoordinate,
  getAddressGeoscape,
  getLandParcelGeoscape,
  getLandParcelByPoint,
  getGeocodeAddressSuggest,
  getPlaceAddressCandidateArcGISWorldGeocoding,
  getBaseMaps
} from 'services/map.service';
import { sortBy } from 'lodash';

const regions: string[] = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

const getThumbnailUrl = (id: string, path: string): string => {
  let isValid = true;
  let urlObj = null;
  try {
    urlObj = new URL(path);
  } catch (e) {
    isValid = false;
  }

  return isValid || urlObj !== null
    ? path
    : `https://smarterwx.maps.arcgis.com/sharing/rest/content/items/${id}/info/${path}?f=json`;
};

function* handleGetGeocode(request: any): any {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    const emptyErrorText =
      'Unable to get address from geocode API. Please retry.';
    try {
      const response = yield call(
        getGeocodeByCoordinate,
        request.payload.datasets,
        request.payload.longitude,
        request.payload.latitude
      );
      if (response?.Results && response.Results instanceof Array) {
        const geocode = response.Results[0];
        if (geocode) {
          yield put(getGeocodeSuccess(geocode));
        } else {
          yield put(getGeocodeFailure(emptyErrorText));
        }
      } else {
        yield put(getGeocodeFailure(emptyErrorText));
      }
    } catch (err) {
      if (err instanceof Error) {
        const msg = err?.message || err.toString();
        put(getGeocodeFailure(msg));
      }
    }
  }
}

function* handleGetPlaceSuggestion(request: any): any {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  try {
    let result: MapPlaceSuggestion[] = [];
    let promise = null;

    if (request.payload.datasets === MAP_DATA_SERVICE_DATA_SET.GNAF) {
      promise = call(
        getGeocodeAddressSuggest,
        request.payload.query,
        !session.isAuthenticated,
        request.payload.limit,
        request.payload.location,
        request.payload.category
      );
    } else {
      promise = call(
        getPlaceSuggest,
        request.payload.datasets,
        request.payload.query,
        request.payload.fields,
        request.payload.limit
      );
    }

    const response = yield promise;

    if (response?.suggest) {
      result = response.suggest.map((suggestion: any) => {
        const addressArr = suggestion.address.split(', ');
        const addressPart2Arr = addressArr[1].split(' ');
        const region = addressPart2Arr.find((a: string) => regions.includes(a));
        const postcode = addressPart2Arr.pop();
        const locality = addressArr[1]
          .replace(`${region} ${postcode}`, '')
          .trim();
        return {
          AddressId: suggestion.id,
          Value: suggestion.address,
          Coordinate: '',
          CustomFields: {
            locality,
            region,
            postcode
          }
        };
      });
    }

    if (response?.Results) {
      result = response.Results;
    } else if (response?.suggestions) {
      result = response.suggestions.map(
        (item: ArcGISWorldGeocodingSuggestionDataModel) => {
          return {
            Value: item.text,
            MagicKey: item.magicKey,
            IsCollection: item.isCollection
          };
        }
      );
    }

    yield put(getPlaceSuggestSuccess(result));
  } catch (err) {
    if (err instanceof Error) {
      const msg = err?.message || err.toString();
      put(getPlaceSuggestFailure(msg));
    }
  }
}

function* handleQueryFeatureLayerExtent(request: any): any {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const response = yield call(
        queryFeatuerLayerExtent,
        request.payload.layer,
        request.payload.query,
        request.payload.extent
      );
      if (response?.count > 0) {
        yield put(queryFeatureLayerExtentSuccess(response.extent));
      } else {
        yield put(queryFeatureLayerExtentSuccess({ noRecord: true }));
      }
    } catch (err) {
      if (err instanceof Error) {
        const msg = err?.message || err.toString();
        yield put(queryFeatureLayerExtentFailure(msg));
      }
    }
  }
}

function* handleQueryFeatureLayerFeatures(request: any): any {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const response: __esri.FeatureSet = yield call(
        queryFeatuerLayerFeatures,
        request.payload.layer,
        request.payload.query
      );
      if (Array.isArray(response.features)) {
        yield put(queryFeatureLayerFeaturesSuccess(response.features));
      }
    } catch (err) {
      if (err instanceof Error) {
        const msg = err?.message || err.toString();
        yield put(queryFeatureLayerFeaturesFailure(msg));
      }
    }
  }
}

function* handleGetHexBinClassBreakStatistics(request: any): any {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      const response: __esri.FeatureSet = yield call(
        getHexBinClassBreakStatistics,
        request.payload.layer,
        request.payload.resolution,
        request.payload.statisticField
      );
      if (Array.isArray(response.features)) {
        const [result] = response.features;
        const classBreaks: HexBinClassBreaks = {
          ...result.attributes
        };
        yield put(getHexBinClassBreakStatisticsSuccess(classBreaks));
      }
    } catch (err) {
      if (err instanceof Error) {
        const msg = err?.message || err.toString();
        yield put(getHexBinClassBreakStatisticsFailure(msg));
      }
    }
  }
}

function* handleGetBaseMaps(): any {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  if (session.isAuthenticated) {
    try {
      // Fetch basemaps from the new feature service
      const response = yield call(getBaseMaps);

      if (response && response.features && Array.isArray(response.features)) {
        // Transform feature service response to BaseMapItem structure
        const results: BaseMapItem[] = response.features.map(function (
          feature: any,
          index: number
        ) {
          const attrs = feature.attributes;

          // Determine theme based on Dark field or MapName
          var theme = 'light';
          if (attrs.Dark === 1) {
            theme = 'dark';
          } else if (
            attrs.MapName &&
            attrs.MapName.toLowerCase().indexOf('dark') !== -1
          ) {
            theme = 'dark';
          }

          // Build thumbnail URL
          const thumbnailUrl =
            attrs.Thumbnail && attrs.MapID
              ? getThumbnailUrl(attrs.MapID, attrs.Thumbnail)
              : '';

          return {
            label: attrs.MapName || 'Unnamed Basemap',
            id: attrs.UniqueKey || attrs.MapID || 'basemap_' + index,
            styleId:
              attrs.StyleId && attrs.StyleId.trim() !== ''
                ? attrs.StyleId
                : null,
            webmapId: attrs.MapID || null,
            thrumbnailUrl: thumbnailUrl,
            theme: theme,
            category: attrs.Category || 1,
            seqNumber: attrs.SeqNumber || index,
            state: attrs.State,
            geometry: feature.geometry
          };
        });

        /**
         * Ensure persisted basemap identifier is still valid.
         * If the current selectedBasemap (from Redux/localStorage) is not
         * present in the latest basemap options from the feature service,
         * fall back to the default basemap (imageryHybrid) so that:
         * - The map uses a valid basemap configuration
         * - The Basemap switcher highlights the correct selection
         *
         * This avoids requiring users to manually clear localStorage or
         * updating LEGACY_WEBMAP_ID_TO_UNIQUE_KEY whenever basemaps change.
         */
        const getApp = (state: any) => state.app;
        const appState = yield select(getApp);
        const currentBasemapId = appState?.selectedBasemap;

        if (currentBasemapId) {
          const currentBasemap = results.find(
            (item) => item.id === currentBasemapId
          );

          if (!currentBasemap) {
            // Prefer the configured default basemap if available, otherwise
            // fall back to the first basemap in the list.
            const fallbackBasemap =
              results.find((item) => item.id === MAP_DEFAULT_VAR.BASEMAP) ||
              results[0];

            if (fallbackBasemap) {
              yield put(setBaseMap(fallbackBasemap.id, fallbackBasemap.theme));
            }
          } else {
            yield put(
              setBaseMap(currentBasemapId, currentBasemap?.theme || 'light')
            );
          }
        }

        // Sort by category and sequence number
        const sortedResults = sortBy(results, ['category', 'seqNumber']);
        yield put(getBaseMapsSuccess(sortedResults));
      } else {
        // No features returned from feature service
        yield put(
          getBaseMapsFailure(
            'No basemaps found in feature service. Please check the dev_Basemap_extents service.'
          )
        );
      }
    } catch (err) {
      // Feature service request failed
      if (err instanceof Error) {
        const msg = err?.message || err.toString();
        yield put(
          getBaseMapsFailure(
            `Failed to load basemaps from feature service: ${msg}`
          )
        );
      } else {
        yield put(
          getBaseMapsFailure(
            'Failed to load basemaps from feature service. Please try again.'
          )
        );
      }
    }
  }
}

function* handleGetPlaceAddressCandidateArcGISWorldGeocoding(
  request: any
): any {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  const sessionAuthenticated = session?.isAuthenticated;
  try {
    const response = yield call(
      getPlaceAddressCandidateArcGISWorldGeocoding,
      request.payload.query,
      request.payload.outFields,
      request.payload.magicKey,
      !sessionAuthenticated
    );

    if (response?.candidates) {
      yield put(getPlaceCandidateAddressSuccess(response?.candidates));
    } else {
      yield put(
        getPlaceCandidateAddressFailure('Unable to get place suggestion.')
      );
    }
  } catch (err) {
    if (err instanceof Error) {
      const msg = err?.message || err.toString();
      put(getPlaceCandidateAddressFailure(msg));
    }
  }
}

function* handleGetGeoscapeAddress(request: any): any {
  const getSession = (state: any) => state.session;
  const session = yield select(getSession);
  try {
    let coordinate;
    const { selectedSuggestion } = request.payload;

    const address = yield call(
      getAddressGeoscape,
      selectedSuggestion.AddressId,
      !session.isAuthenticated
    );
    if (address?.address?.geometry) {
      coordinate = address.address.geometry.coordinates.join(',');
    }

    yield put(
      getGeoscapeAddressSuccess({
        coordinate,
        selectedSuggestion,
        address: {
          line1: selectedSuggestion.Value.replace(
            `, ${selectedSuggestion.CustomFields.locality} ${selectedSuggestion.CustomFields.region} ${selectedSuggestion.CustomFields.postcode}`,
            ''
          ),
          line2: '',
          locality: selectedSuggestion.CustomFields.locality,
          state: selectedSuggestion.CustomFields.region,
          postcode: selectedSuggestion.CustomFields.postcode,
          country: 'AUS'
        }
      })
    );
  } catch (err) {
    if (err instanceof Error) {
      const msg = err?.message || err.toString();
      put(getGeoscapeAddressFailure(msg));
    }
  }
}

// Geoscape approach types
const GEOSCAPE_APPROACH_ORIGINAL = 'original';
const GEOSCAPE_APPROACH_REPLACE = 'replace';
const GEOSCAPE_APPROACH_FALLBACK = 'fallback';

function* handleGetGeoscapeLandParcel(request: any): any {
  const getSession = (state: any) => state.session;
  const getMapState = (state: any) => state.mapCache;
  const session = yield select(getSession);
  const mapState = yield select(getMapState);
  try {
    let landParcel;
    const { selectedSuggestion } = request.payload;

    // Get the current approach - use localStorage in debug mode, otherwise default to REPLACE
    const approach =
      process.env.REACT_APP_GEOSCAPE_DEBUG === 'true'
        ? localStorage.getItem('geoscape-test-approach') ||
          GEOSCAPE_APPROACH_REPLACE
        : GEOSCAPE_APPROACH_REPLACE;

    const processLandParcelResult = (features: any[]) => {
      if (
        features[0].geometry.type === 'MultiPolygon' &&
        features[0].geometry.coordinates[0].length > 1
      ) {
        return {
          shape: 'no shape'
        };
      } else {
        return {
          shape: features[0].geometry,
          formattedaddress: selectedSuggestion.Value,
          address: {
            line1: selectedSuggestion.Value.replace(
              `, ${selectedSuggestion.CustomFields.locality} ${selectedSuggestion.CustomFields.region} ${selectedSuggestion.CustomFields.postcode}`,
              ''
            ),
            line2: '',
            locality: selectedSuggestion.CustomFields.locality,
            state: selectedSuggestion.CustomFields.region,
            postcode: selectedSuggestion.CustomFields.postcode,
            country: 'AUS'
          }
        };
      }
    };

    const getCoordinatesAndCallFindByPoint = function* (): any {
      // Check if we already have coordinates in Redux state
      const storedGeoscapeAddress = mapState?.geoscapeAddress;

      if (
        storedGeoscapeAddress?.coordinate &&
        storedGeoscapeAddress?.selectedSuggestion?.AddressId ===
          selectedSuggestion.AddressId
      ) {
        // Use already available coordinates from Redux state - saves 2 credits!
        const [longitude, latitude] = storedGeoscapeAddress.coordinate
          .split(',')
          .map(Number);
        console.log(
          'Using cached coordinates from Redux state, saving 2 credits!'
        );
        return yield call(
          getLandParcelByPoint,
          latitude,
          longitude,
          !session.isAuthenticated
        );
      } else {
        // Fallback to API call if coordinates not available in state
        console.log(
          'Coordinates not available in state, making API call to getAddressGeoscape'
        );
        const address = yield call(
          getAddressGeoscape,
          selectedSuggestion.AddressId,
          !session.isAuthenticated
        );

        if (address?.address?.geometry?.coordinates) {
          const [longitude, latitude] = address.address.geometry.coordinates;
          return yield call(
            getLandParcelByPoint,
            latitude,
            longitude,
            !session.isAuthenticated
          );
        }
      }
      return null;
    };

    switch (approach) {
      case GEOSCAPE_APPROACH_ORIGINAL: {
        // Original approach: findByIdentifier only
        const originalResult = yield call(
          getLandParcelGeoscape,
          selectedSuggestion.AddressId,
          !session.isAuthenticated
        );

        if (
          Array.isArray(originalResult?.features) &&
          originalResult.features.length > 0
        ) {
          landParcel = processLandParcelResult(originalResult.features);
        } else {
          landParcel = { shape: 'no shape' };
        }
        break;
      }

      case GEOSCAPE_APPROACH_REPLACE: {
        // Replace approach: findByPoint only
        try {
          const replaceResult = yield call(getCoordinatesAndCallFindByPoint);

          if (
            Array.isArray(replaceResult?.features) &&
            replaceResult.features.length > 0
          ) {
            landParcel = processLandParcelResult(replaceResult.features);
          } else {
            landParcel = { shape: 'no shape' };
          }
        } catch (replaceErr) {
          landParcel = { shape: 'no shape' };
        }
        break;
      }

      case GEOSCAPE_APPROACH_FALLBACK: {
        // Fallback approach: findByIdentifier first, then findByPoint if needed
        const fallbackResult = yield call(
          getLandParcelGeoscape,
          selectedSuggestion.AddressId,
          !session.isAuthenticated
        );

        if (
          Array.isArray(fallbackResult?.features) &&
          fallbackResult.features.length > 0
        ) {
          // Primary approach succeeded
          landParcel = processLandParcelResult(fallbackResult.features);
        } else {
          // Fallback to coordinate-based lookup using findByPoint
          try {
            const fallbackPointResult = yield call(
              getCoordinatesAndCallFindByPoint
            );

            if (
              Array.isArray(fallbackPointResult?.features) &&
              fallbackPointResult.features.length > 0
            ) {
              // Fallback approach succeeded
              landParcel = processLandParcelResult(
                fallbackPointResult.features
              );
            } else {
              // Both approaches failed
              landParcel = { shape: 'no shape' };
            }
          } catch (fallbackErr) {
            // Fallback failed
            landParcel = { shape: 'no shape' };
          }
        }
        break;
      }

      default: {
        // Default to original approach
        const defaultResult = yield call(
          getLandParcelGeoscape,
          selectedSuggestion.AddressId,
          !session.isAuthenticated
        );

        if (
          Array.isArray(defaultResult?.features) &&
          defaultResult.features.length > 0
        ) {
          landParcel = processLandParcelResult(defaultResult.features);
        } else {
          landParcel = { shape: 'no shape' };
        }
        break;
      }
    }

    yield put(getGeoscapeLandParcelSuccess(landParcel));
  } catch (err) {
    if (err instanceof Error) {
      const msg = err?.message || err.toString();
      put(getGeoscapeLandParcelFailure(msg));
    }
  }
}

function* watchGetGeocode() {
  yield takeLatest(MapActionTypes.GET_GEOCODE_START, handleGetGeocode);
}

function* watchGetPlaceSuggestionRequest() {
  yield takeLatest(
    MapActionTypes.GET_PLACE_SUGGESTION_START,
    handleGetPlaceSuggestion
  );
}

function* watchQueryFeatureLayerExtent() {
  yield takeLatest(
    MapActionTypes.QUERY_FEATURE_LAYER_EXTENT_START,
    handleQueryFeatureLayerExtent
  );
}

function* watchQueryFeatureLayerFeatures() {
  yield takeLatest(
    MapActionTypes.QUERY_FEATURE_LAYER_FEATURES_START,
    handleQueryFeatureLayerFeatures
  );
}

function* watchGetHexBinClassBreakStatistics() {
  yield takeLatest(
    MapActionTypes.GET_HEXBIN_CLASS_BREAKS_START,
    handleGetHexBinClassBreakStatistics
  );
}

function* watchGetBaseMapsRequest() {
  yield takeLatest(MapActionTypes.GET_BASE_MAPS_START, handleGetBaseMaps);
}

function* watchGetPlaceAddressCandidate() {
  yield takeLatest(
    MapActionTypes.GET_PLACE_ADDRESS_CANDIDATE_START,
    handleGetPlaceAddressCandidateArcGISWorldGeocoding
  );
}

function* watchGeoscapeAddress() {
  yield takeLatest(
    MapActionTypes.GET_GEOSCAPE_ADDRESS_START,
    handleGetGeoscapeAddress
  );
}

function* watchGeoscapeLandParcel() {
  yield takeLatest(
    MapActionTypes.GET_GEOSCAPE_LAND_PARCEL_START,
    handleGetGeoscapeLandParcel
  );
}

function* mapSagas() {
  yield all([
    call(watchGetPlaceSuggestionRequest),
    call(watchQueryFeatureLayerExtent),
    call(watchQueryFeatureLayerFeatures),
    call(watchGetHexBinClassBreakStatistics),
    call(watchGetGeocode),
    call(watchGetBaseMapsRequest),
    call(watchGeoscapeAddress),
    call(watchGeoscapeLandParcel),
    call(watchGetPlaceAddressCandidate)
  ]);
}
export default mapSagas;
